import type { ReactNode } from 'react';

// ============================================================================
// FieldRow
// ----------------------------------------------------------------------------
// Two-column layout: label on left, value on right. Used inside FieldGroup
// sections in the detail view. Visual density tuned for scanning many fields
// at once — label ~30% width, value ~70%.
//
// Pass `editor` to render an input in edit mode; otherwise the value renders
// as-is. The component itself doesn't know about edit mode; the parent
// passes either the rendered value or the rendered editor.
// ============================================================================

interface Props {
    label: string;
    children: ReactNode;
    helpText?: string;
    fullWidth?: boolean;  // Some fields (like description, notes) want full width
}

export default function FieldRow({ label, children, helpText, fullWidth }: Props) {
    if (fullWidth) {
        return (
            <div className="py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-pt-text-dim font-semibold mb-1.5">
                    {label}
                </div>
                <div className="text-sm text-pt-text">{children}</div>
                {helpText && (
                    <div className="text-xs text-pt-text-dim mt-1">{helpText}</div>
                )}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-[160px_1fr] gap-4 py-2.5 items-start">
            <div className="text-[10px] uppercase tracking-wider text-pt-text-dim font-semibold pt-1">
                {label}
            </div>
            <div className="min-w-0">
                <div className="text-sm text-pt-text">{children}</div>
                {helpText && (
                    <div className="text-xs text-pt-text-dim mt-1">{helpText}</div>
                )}
            </div>
        </div>
    );
}

/** Empty-value placeholder — used when a field is null in read mode. */
export function EmptyValue() {
    return <span className="text-pt-text-dim">—</span>;
}

/** Monospace value, used for asset tags, serial numbers, codes. */
export function MonoValue({ children }: { children: ReactNode }) {
    return (
        <span className="font-mono text-pt-text-muted text-xs">{children}</span>
    );
}

/** Currency formatter, USD only in v1. */
export function formatCurrency(value: number | null | undefined): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
}

/** Date formatter — short style, locale-aware. */
export function formatDate(date: string | null | undefined): string {
    if (!date) return '—';
    try {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return date;
    }
}

/** Date+time formatter. */
export function formatDateTime(date: string | null | undefined): string {
    if (!date) return '—';
    try {
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    } catch {
        return date;
    }
}

/** Boolean formatter — "Yes"/"No" rendering. */
export function formatBoolean(value: boolean | null | undefined): string {
    if (value == null) return '—';
    return value ? 'Yes' : 'No';
}
